/* eslint-disable no-underscore-dangle */
import type { CreateAccountParams } from '@application/api';
import { useCreateAccount } from '@application/api';
import type { JoinBuyerFromControllerProps } from '@application/components';
import { ENV, environmentVariable } from '@application/env';
import { formatPhoneNumber, scrollToError, useRouter } from '@application/utils';
import type { CountryCode } from '@gql/graphql';
import { AccountCreatedFromEnum } from '@gql/graphql';
import type { JoinBuyerFormValues } from '@presentation/components';
import { JoinBuyerForm } from '@presentation/components';
import type { TokenResponse } from '@react-oauth/google';
import { useGoogleLogin } from '@react-oauth/google';
import { FormikProvider, useFormik } from 'formik';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { ReactFacebookLoginInfo, ReactFacebookLoginProps } from 'react-facebook-login';
import FacebookLogin from 'react-facebook-login/dist/facebook-login-render-props';
import { useValidationSchema } from './useValidationSchema';

export * from './types';

export const JoinBuyerFormController: FC<JoinBuyerFromControllerProps> = (props) => {
  const {
    loginLink,
    createdFrom = AccountCreatedFromEnum.SIGNUP,
    hasADog,
    requireMarketingEmailConsent = false,
  } = props;
  // We want to collect phone numbers when users signup using FB or GOOGLE
  const [showPostSignup, setShowPostSignup] = useState<boolean>(false);
  const [tempAccountParams, setTempAccountParams] = useState<CreateAccountParams | null>(null);
  const { infoFromCurrentPath, country } = useRouter();
  const { validationSchema } = useValidationSchema({
    createdFrom,
    showPostSignup,
  });

  const initialValues: JoinBuyerFormValues = useMemo(() => {
    return {
      email: (infoFromCurrentPath.search.email as string) ?? '',
      firstName: '',
      lastName: '',
      phonePrefixCountry: country.toUpperCase(),
      phoneNumber: '',
      country: country.toUpperCase() as CountryCode,
      password: '',
      confirmPassword: '',
      acceptedTerms: false,
      createdFrom,
      hasADog,
      marketingEmailConsent: !requireMarketingEmailConsent,
    };
  }, [
    country,
    createdFrom,
    hasADog,
    infoFromCurrentPath.search.email,
    requireMarketingEmailConsent,
  ]);

  const { createAccount, createAccountLoading } = useCreateAccount();

  const googleAuthHandler = useCallback(async (response: TokenResponse) => {
    const userInfo = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
      headers: { Authorization: `Bearer ${response.access_token}` },
    }).then((res) => res.json());

    if (!userInfo) {
      return;
    }

    setTempAccountParams({
      googleToken: response.access_token,
      email: userInfo.email,
      lastName: userInfo.family_name,
      firstName: userInfo.given_name,
      imgUrl: userInfo.picture,
    });

    setShowPostSignup(true);
  }, []);

  const facebookAuthHandler: ReactFacebookLoginProps['callback'] = useCallback(
    async (response: any) => {
      const facebookToken = (response as ReactFacebookLoginInfo).accessToken;
      setTempAccountParams({
        facebookToken,
        email: response.email,
        firstName: response.first_name ?? '',
        lastName: response.last_name ?? '',
        imgUrl: response.picture.data.url,
      });

      setShowPostSignup(true);
    },
    [],
  );

  const signIn = useGoogleLogin({
    onSuccess: googleAuthHandler as any,
  });

  const createNewAccount = useCallback(
    (params: { values: JoinBuyerFormValues }) => {
      const { values } = params;
      if (tempAccountParams) {
        createAccount({
          ...tempAccountParams,
          phone: formatPhoneNumber(values.phoneNumber, values.phonePrefixCountry),
          country: values.country,
          createdFrom,
          hasADog: values.hasADog,
          marketingEmailConsent: values.marketingEmailConsent,
        });

        return;
      }

      return createAccount({
        buyerFormValues: values,
        phone: formatPhoneNumber(values.phoneNumber, values.phonePrefixCountry),
        country: values.country,
        createdFrom,
        hasADog: values.hasADog,
        marketingEmailConsent: values.marketingEmailConsent,
      });
    },
    [tempAccountParams, createAccount, createdFrom],
  );

  const form = useFormik({
    initialValues,
    onSubmit: async (values) => {
      createNewAccount({ values });
    },
    validationSchema,
    enableReinitialize: true,
  });

  const onSubmitClickHandler = useCallback(async () => {
    await form.submitForm();
    scrollToError(form.errors);
  }, [form]);

  const isLoading = useMemo(() => {
    return createAccountLoading || form.isSubmitting;
  }, [createAccountLoading, form.isSubmitting]);

  const showHasADogField = useMemo(() => {
    return createdFrom === AccountCreatedFromEnum.SIGNUP;
  }, [createdFrom]);

  return (
    <>
      <FacebookLogin
        version="4.0"
        appId={environmentVariable(ENV.FACEBOOK_ID)}
        fields="first_name,last_name,email,picture"
        callback={facebookAuthHandler}
        render={(renderProps: ReactFacebookLoginProps) => (
          <FormikProvider value={form}>
            <JoinBuyerForm
              loginLink={loginLink}
              loading={isLoading}
              onFacebookClick={renderProps.onClick}
              onGoogleClick={() => signIn()}
              showPostSignup={showPostSignup}
              onSubmitClick={onSubmitClickHandler}
              showHasADogField={showHasADogField}
              requireMarketingEmailConsent={requireMarketingEmailConsent}
            />
          </FormikProvider>
        )}
      />
    </>
  );
};
